import BlogCard from '@/components/blog/BlogCard';
import { Input } from '@/components/ui/input';
import { MotionButton } from '@/components/ui/motion-button';
import { useBlogQueries } from '@/hooks/queries/useBlogQueries';
import { useAuth } from '@/hooks/useAuth';
import { slideInFromBottom, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { setPageMetadata } from '@/lib/utils';
import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { FileText, Plus, Search, Tag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/_public/blog/')({
  component: BlogListPage,
})

export default function BlogListPage() {
  useEffect(() => {
    setPageMetadata({
      title: 'Blog',
      description: 'Explore articles and guides about home selling, real estate tips, and how AI is transforming the property market in Canada. Save on commissions with Swim Homes.',
    });
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const { profile } = useAuth();
  const { useFetchBlogPosts } = useBlogQueries();
  const { data: posts = [], isLoading } = useFetchBlogPosts();

  // Extract all unique tags from posts
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    posts.forEach(post => post.tags.forEach(tag => tags.add(tag)));
    return Array.from(tags);
  }, [posts]);

  const filteredPosts = posts.filter(post =>
    (searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    ) &&
    (selectedTag === null || post.tags.includes(selectedTag))
  );

  return (
    <>
      <div className="min-h-screen bg-white relative z-10">
        {/* Hero Section with Gradient Background */}
        <section className="relative bg-linear-to-br from-[#0074b7] to-[#0057a7] py-16 pb-32 md:pb-40">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <motion.span
                  variants={staggerItem}
                  className="inline-block px-4 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-6"
                >
                  Our Blog
                </motion.span>
                <motion.h1
                  variants={staggerItem}
                  className="text-4xl md:text-5xl font-bold text-white mb-6"
                >
                  Insights For <span className="text-blue-200">Canadian</span> Home Sellers
                </motion.h1>
                <motion.p
                  variants={staggerItem}
                  className="text-lg md:text-xl text-blue-100 mb-8 max-w-2xl mx-auto"
                >
                  Latest insights, updates, and guides to help you sell your home
                </motion.p>
                <motion.div
                  variants={staggerItem}
                  className="relative w-full max-w-md mx-auto mb-8"
                >
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    placeholder="Search for articles..."
                    className="pl-12 pr-4 py-3 rounded-full bg-white/95 text-black shadow-md border-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Wave divider */}
          <div className="absolute -bottom-1 left-0 w-full overflow-hidden leading-[0] z-10">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[60px] md:h-[100px] text-white" style={{ display: 'block' }}>
              <path d="M0,0V30c180,60,420,-40,600,10c100,30,220,70,350,30c80,-25,170,-50,250,-10V120H0Z" fill="currentColor"></path>
            </svg>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12 relative z-20 -mt-10">
          <div className="max-w-6xl mx-auto">
            {/* Category Filter */}
            <motion.div
              variants={slideInFromBottom}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOptions}
            >
              <div className="mb-10 overflow-x-auto">
                <div className="flex space-x-2 pb-2">
                  <MotionButton
                    variant={selectedTag === null ? "default" : "outline"}
                    className="rounded-full shadow-sm"
                    onClick={() => setSelectedTag(null)}
                  >
                    All Topics
                  </MotionButton>
                  {allTags.map(tag => (
                    <MotionButton
                      key={tag}
                      variant={selectedTag === tag ? "default" : "outline"}
                      className="rounded-full whitespace-nowrap shadow-sm"
                      onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                    >
                      <Tag className="h-4 w-4 mr-2" />
                      {tag}
                    </MotionButton>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Admin Button */}
            {profile?.swimhomes_admin && (
              <div className="flex justify-end mb-6">
                <a href="/blog/new">
                  <MotionButton>
                    <Plus className="h-4 w-4 mr-2" />
                    New Post
                  </MotionButton>
                </a>
              </div>
            )}

            {/* Blog Posts */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 aspect-video rounded-lg mb-4" />
                    <div className="space-y-3">
                      <div className="h-6 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-full" />
                      <div className="h-4 bg-gray-200 rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post, index) => (
                  <motion.div
                    key={post.slug}
                    variants={slideInFromBottom}
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportOptions}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => {
                      window.location.href = `/blog/${post.slug}`;
                    }}
                    className="group block cursor-pointer"
                  >
                    <BlogCard post={post} />
                  </motion.div>
                ))
                }</div>
            ) : (
              <div className="text-center py-16 bg-gray-50 rounded-lg">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-medium mb-2">No posts found</h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery || selectedTag
                    ? "No posts match your search criteria"
                    : "Check back later for new content"}
                </p>
                {(searchQuery || selectedTag) && (
                  <MotionButton onClick={() => { setSearchQuery(''); setSelectedTag(null); }}>
                    Clear Filters
                  </MotionButton>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
